import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";

/**
 * Wraps public pages so:
 *  - Logged-in users see the standard app sidebar + topbar around the page content
 *    (so they never get stranded on public pages like /downloads without navigation).
 *  - Anonymous visitors (e.g. shared links, SEO) see the page as-is, with a lightweight
 *    public top strip that lets them get back to the site.
 */
export default function AuthAwareShell({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div data-testid="auth-shell-loading" className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-[#64748B] text-sm tracking-wide">Surfacing…</div>
      </div>
    );
  }

  if (user) {
    return <AppLayout>{children}</AppLayout>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="auth-shell-public">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-shell-logo">
            <div className="w-8 h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
              <span className="text-[#4FD1C5] font-heading font-bold text-sm">D</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-[14px] text-[#0A2342]">Dive Media</div>
              <div className="text-[10px] tracking-[0.22em] text-[#64748B] uppercase mt-0.5">Strategist</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/downloads" data-testid="public-shell-resources" className="hidden sm:inline-flex text-[12.5px] font-semibold text-[#1F2937] hover:text-[#0A2342] px-3 py-1.5 rounded-md hover:bg-[#F1F5F9]">Resources</Link>
            <Link to="/pro-tools" data-testid="public-shell-pro-tools" className="hidden sm:inline-flex text-[12.5px] font-semibold text-[#1F2937] hover:text-[#0A2342] px-3 py-1.5 rounded-md hover:bg-[#F1F5F9]">Pro tools</Link>
            <Link to="/login" data-testid="public-shell-login" className="text-[12.5px] font-semibold text-[#1F2937] hover:text-[#0A2342] px-3 py-1.5 rounded-md hover:bg-[#F1F5F9]">Sign in</Link>
            <Link to="/register" data-testid="public-shell-register" className="text-[12.5px] font-semibold text-white bg-[#0A2342] hover:bg-[#0F2D50] px-3 py-1.5 rounded-md">Create account</Link>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
